// src/services/productAPI.ts
// Bridges frontend product items (IndexedDB shape) to backend REST API.
// Called by useProductManager when MOCKS_ENABLED=false.

import { fetchWithAuth } from './auth';
import { STORE_NAMES } from '../utils/db';
import type { StoreName } from '../utils/db';
import { API_BASE } from '../config';

// ── Store → backend category name ──────────────────────────────────────────

const STORE_TO_CATEGORY: Partial<Record<StoreName, string>> = {
  [STORE_NAMES.sparkPlugs]:     'spark_plugs',
  [STORE_NAMES.coils]:          'ignition_coils',
  [STORE_NAMES.steeringClocks]: 'airbag_cables',
  [STORE_NAMES.wires]:          'ignition_coil_mouthpieces',
};

// Sensor types that belong to crankshaft category; everything else → camshaft (misc)
const CRANKCAM_SENSOR_TYPES = new Set(['Коленвал', 'Распредвал']);

/**
 * Derives the MySQL category for a sensor item from sensorType.
 * Коленвал / Распредвал → crankshaft_sensors ("Коленвал - Распредвал")
 * Everything else        → camshaft_sensors  ("Разные")
 */
function getSensorCategory(item: Record<string, any>): string {
  return CRANKCAM_SENSOR_TYPES.has(String(item.sensorType ?? ''))
    ? 'crankshaft_sensors'
    : 'camshaft_sensors';
}

// ── Seat type: frontend "шайба"/"конус" → DB "шайбовый"/"конический" ───────

const SEAT_TYPE_TO_DB: Record<string, string> = {
  'шайба': 'шайбовый',
  'конус':  'конический',
};

// ── Refs / OEMs format conversion ──────────────────────────────────────────

type BackendRef = { ref_num: string; brand: string };
type BackendOem = { oem: string; model: string };

function refsToBackend(refs: Record<string, string[]> | undefined): BackendRef[] {
  if (!refs) return [];
  return Object.entries(refs).flatMap(([brand, codes]) =>
    codes.map((code) => ({ ref_num: code, brand })),
  );
}

function oemsToBackend(oems: Record<string, string[]> | undefined): BackendOem[] {
  if (!oems) return [];
  return Object.entries(oems).flatMap(([model, numbers]) =>
    numbers.map((oem) => ({ oem, model })),
  );
}

// ── Category-specific fields ───────────────────────────────────────────────

// importMappers.ts stores UI display values with "мм" suffix (e.g. "16мм").
// Backend expects plain numbers — strip the suffix before sending.
const stripMm = (v: unknown): string => String(v ?? '').replace(/мм$/, '');

function appendCategoryFields(fd: FormData, item: Record<string, any>, cat: string) {
  switch (cat) {
    case 'spark_plugs':
      fd.append('electrode_type',    String(item.electrodeType   ?? ''));
      fd.append('electrodes_number', String(item.electrodeCount  ?? 1));
      fd.append('key_type',          String(item.hexType         ?? ''));
      fd.append('key_size',          stripMm(item.hexSize));
      fd.append('seat_type',         SEAT_TYPE_TO_DB[item.seatType] ?? String(item.seatType ?? ''));
      fd.append('thread_size',       stripMm(item.threadSize));
      fd.append('thread_length',     stripMm(item.threadLength));
      fd.append('gap',               String(item.gap             ?? ''));
      fd.append('heat_rating',       String(item.heatRating      ?? ''));
      break;

    case 'ignition_coils':
      fd.append('plugs_number',    String(item.connectorCount ?? 0));
      fd.append('contacts_number', String(item.contactCount   ?? 0));
      break;

    case 'crankshaft_sensors':
    case 'camshaft_sensors': {
      // contact_number ENUM('1','2','3') — clamp to valid values
      const rawContacts = String(Number(item.contactsCount) || 1);
      const contactsCount = ['1','2','3'].includes(rawContacts) ? rawContacts : '1';
      fd.append('connectorType',  String(item.connectorType  ?? ''));
      fd.append('contactsCount',  contactsCount);
      fd.append('connectionType', String(item.connectionType ?? 'wired'));
      fd.append('sensor_type',    String(item.sensorType     ?? ''));
      break;
    }

    case 'airbag_cables':
      fd.append('steering_axle_bore_diameter', String(item.connectorCount  ?? 0));
      fd.append('airbag_plugs_number',         String(item.airbagWireCount ?? 0));
      break;

    case 'ignition_coil_mouthpieces': {
      fd.append('type_', String(item.tipType ?? ''));
      const rawLen = item.length;
      if (rawLen != null && rawLen !== '') {
        const normalized = parseFloat(String(rawLen).replace(',', '.'));
        if (!isNaN(normalized)) fd.append('length', String(normalized));
      }
      break;
    }
  }
}

// ── Build FormData from frontend item ──────────────────────────────────────

function itemToFormData(item: Record<string, any>, cat: string): FormData {
  const fd = new FormData();
  const isOriginal = item.original === 'original';

  fd.append('category_name',  cat);
  fd.append('brand',          String(item.brand       ?? ''));
  fd.append('model',          String(item.model       ?? ''));
  fd.append('detail_number',  String(item.partNumber  ?? ''));
  fd.append('sku',            String(item.sku         ?? ''));

  // price and quantity split into original/copy columns
  fd.append('price_original', isOriginal ? String(item.price    ?? 0) : '0');
  fd.append('price_copy',     isOriginal ? '0' : String(item.price    ?? 0));
  fd.append('count_original', isOriginal ? String(item.quantity ?? 0) : '0');
  fd.append('count_copy',     isOriginal ? '0' : String(item.quantity ?? 0));

  fd.append('discount',    '0');
  fd.append('top_selling', '0');

  // refs/oems as JSON strings — backend calls JSON.parse() on them
  fd.append('refs',         JSON.stringify(refsToBackend(item.references)));
  fd.append('oems',         JSON.stringify(oemsToBackend(item.oems)));
  // Strip API origin from URLs — MySQL stores relative paths ("products/uuid.jpg"),
  // but IndexedDB holds full URLs ("http://localhost:5000/products/uuid.jpg").
  const apiOrigin = API_BASE.replace(/\/api$/, '');
  const deletedImgs = (Array.isArray(item._deletedImgs) ? item._deletedImgs : [])
    .map((url: string) => url.startsWith(apiOrigin + '/') ? url.slice(apiOrigin.length + 1) : url);
  fd.append('deleted_imgs', JSON.stringify(deletedImgs));

  // ── Images: send only new uploads (base64 strings), skip existing server paths ──
  const images: string[] = Array.isArray(item.images) ? item.images : [];
  const newImgs = images.filter((src: string) => typeof src === 'string' && src.startsWith('data:'));
  newImgs.forEach((src: string, i: number) => {
    fd.append('imgs', base64ToFile(src, `img_${i}`));
  });
  // img_master: first uploaded image is the master (index 0)
  fd.append('img_master', newImgs.length > 0 ? '0' : '-1');

  appendCategoryFields(fd, item, cat);

  return fd;
}

// ── Images: base64 data-URL → File (for FormData upload) ──────────────────────

/**
 * Converts a base64 data-URL string to a File object suitable for FormData.
 * Only call this for strings that start with "data:".
 */
function base64ToFile(dataUrl: string, baseName: string): File {
  const [header, b64] = dataUrl.split(',');
  const mimeMatch = header.match(/data:([^;]+);/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const ext = mime.split('/')[1] || 'jpg';
  return new File([bytes], `${baseName}.${ext}`, { type: mime });
}

// ── Error helper ───────────────────────────────────────────────────────────

async function extractError(res: Response, fallback: string): Promise<string> {
  try {
    const d = await res.json() as { message?: string; errors?: Record<string, string> };
    const fields = d.errors ? Object.entries(d.errors).map(([k, v]) => `${k}: ${v}`).join('; ') : '';
    return fields ? `${d.message || fallback} → ${fields}` : (d.message || fallback);
  } catch {
    return fallback;
  }
}

// ── Public API ─────────────────────────────────────────────────────────────

export function isMysqlBacked(item: { id?: string | number }): boolean {
  const n = Number(item.id);
  return Number.isInteger(n) && n > 0;
}

/**
 * Fetches fresh refs and oems for a MySQL-backed product directly from the backend.
 * Used by form components to pick up data inserted directly into MySQL (bypassing IndexedDB).
 * Returns null on network error or if the backend is offline — caller falls back to IndexedDB.
 */
export async function fetchProductRefsOems(
  categoryName: string,
  productId: number,
): Promise<{ references: Record<string, string[]>; oems: Record<string, string[]> } | null> {
  try {
    const res = await fetch(`${API_BASE}/product/${categoryName}/${productId}`);
    if (!res.ok) return null;
    const data = await res.json() as { refs?: any[]; oems?: any[] };

    const references: Record<string, string[]> = {};
    for (const r of (data.refs ?? [])) {
      (references[r.brand] ??= []).push(r.ref_num);
    }
    const oems: Record<string, string[]> = {};
    for (const o of (data.oems ?? [])) {
      (oems[o.model] ??= []).push(o.oem);
    }
    return { references, oems };
  } catch {
    return null;
  }
}

function resolveCat(item: Record<string, any>, storeName: StoreName): string {
  if (storeName === STORE_NAMES.sensors) return getSensorCategory(item);
  const cat = STORE_TO_CATEGORY[storeName];
  if (!cat) throw new Error(`Категория не найдена для хранилища: ${storeName}`);
  return cat;
}

export async function apiCreateProduct(
  item: Record<string, any>,
  storeName: StoreName,
): Promise<string> {
  const cat = resolveCat(item, storeName);

  const res = await fetchWithAuth(`${API_BASE}/product`, {
    method: 'POST',
    body:   itemToFormData(item, cat),
  });

  if (!res.ok) throw new Error(await extractError(res, `Ошибка создания товара: ${res.status}`));

  const data = await res.json() as { product_id?: number };
  return data.product_id ? String(data.product_id) : item.id;
}

export async function apiUpdateProduct(
  item: Record<string, any>,
  storeName: StoreName,
): Promise<void> {
  const cat = resolveCat(item, storeName);

  if (!isMysqlBacked(item)) {
    throw new Error('Товар не синхронизирован с сервером — обновление в MySQL невозможно');
  }

  const res = await fetchWithAuth(`${API_BASE}/product/${Number(item.id)}`, {
    method: 'PATCH',
    body:   itemToFormData(item, cat),
  });

  if (!res.ok) throw new Error(await extractError(res, `Ошибка обновления товара: ${res.status}`));
}

/**
 * Toggles a product's visibility in the admin catalogue (hidden = true/false).
 * Lightweight single-column update — does not touch category fields or images.
 */
export async function apiSetProductVisibility(
  productId: string | number,
  hidden: boolean,
): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/product/${Number(productId)}/visibility`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ hidden }),
  });
  if (!res.ok) throw new Error(await extractError(res, `Ошибка изменения видимости: ${res.status}`));
}

/**
 * Поиск для «Продажи» и «Приёмки»: штрихкод, номер детали или модель с коробки.
 * В каждой строке matched_by: 'sku' (по штрихкоду), 'number' (номер / модель),
 * 'partial' (часть номера).
 */
export async function apiSearchBySku(sku: string): Promise<any[]> {
  const res = await fetch(`${API_BASE}/product/search?sku=${encodeURIComponent(sku)}`);
  if (!res.ok) throw new Error(`Ошибка поиска: ${res.status}`);
  return res.json();
}

/** Привязать отсканированный штрихкод к карточке — следующий скан найдёт её сразу. */
export async function apiLinkBarcode(productId: number | string, sku: string): Promise<void> {
  const res = await fetchWithAuth(`${API_BASE}/product/${Number(productId)}/sku`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sku }),
  });
  if (!res.ok) throw new Error(await extractError(res, `Ошибка привязки штрихкода: ${res.status}`));
}

export async function apiDeleteProduct(
  item: Record<string, any>,
  storeName: StoreName,
): Promise<void> {
  const cat = resolveCat(item, storeName);

  // Locally-created products (UUID) have no MySQL row — nothing to delete on server
  if (!isMysqlBacked(item)) return;

  const res = await fetchWithAuth(
    `${API_BASE}/product/${cat}/${Number(item.id)}`,
    { method: 'DELETE' },
  );

  if (!res.ok) throw new Error(await extractError(res, `Ошибка удаления товара: ${res.status}`));
}
